import { FC, useContext } from 'react';
import { useLocalizedStrings } from '../../../../Localization';
import InterfaceContext from '../../../../Context';
import { snakeToEnglish } from '../../../../Util';
import { ApplicantInfoSectionProps } from '../shared/types';
import { InfoField } from './InfoField';

/**
 * Applicant info section showing detailed applicant information fields
 */
export const ApplicantInfoSection: FC<ApplicantInfoSectionProps> = ({
  applicantInfo,
  applicantUid,
  infoDefs
}) => {
  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);

  // Get filtered keys
  const filteredKeys = applicantInfo && infoDefs
    ? Object.keys(applicantInfo).filter((key) => !['legal_name', 'phone_number', 'email'].includes(key))
    : [];

  // Don't render if there's no applicant UID and no filtered fields
  if (!applicantUid && filteredKeys.length === 0) {
    return null;
  }

  return (
    <div className="min-w-0">
      <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">{L.support.applicant_info}</h3>
      <div className="space-y-3 min-w-0">
        {/* Applicant ID */}
        {applicantUid && (
          <InfoField
            label={L.applicant.application_id}
            value={applicantUid}
          />
        )}

        {/* Other applicant info fields */}
        {filteredKeys
          .sort((a, b) => {
            let aIndex = infoDefs![a]?.index || 0;
            let bIndex = infoDefs![b]?.index || 0;
            return aIndex - bIndex;
          })
          .map((infoKey) => {
            const fieldContent = infoDefs![infoKey]?.content;
            const displayLabel = fieldContent && typeof fieldContent === 'object'
              ? (fieldContent as any)[context.lang] || snakeToEnglish(infoKey)
              : snakeToEnglish(infoKey);

            return (
              <InfoField
                key={infoKey}
                label={displayLabel}
                value={applicantInfo![infoKey] || '-'}
              />
            );
          })}
      </div>
    </div>
  );
};
